import { ChangeEvent, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { localizeApiMessage } from "../../errors/messages";
import { getExpressCreditBills } from "../../services/expressDocument";
import { apiErrorMessage } from "../SpecialOrder/data";
import { ExpressCreditBill, ExpressCreditBillParams } from "../../types/express-document.type";
import { formatNumber } from "../../utils";
import { formatBuddhistShortDate } from "../../utils/date";
import { gridHeadCellSx, gridHeadSx } from "./gridStyles";

const FALLBACK_ERROR = "โหลดบิลเครดิตไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

interface CreditBillDialogProps {
  open: boolean;
  /** Whose bills, in which branch, leaving out the receipt being edited. */
  params: ExpressCreditBillParams;
  /** Bills already on the receipt; they are not offered again. */
  pickedBillNos: string[];
  onClose: () => void;
  onPick: (bills: ExpressCreditBill[]) => void;
}

function CreditBillRow({
  bill,
  isChecked,
  onToggle,
}: Readonly<{ bill: ExpressCreditBill; isChecked: boolean; onToggle: (billNo: string) => void }>) {
  const handleToggle = (_event: ChangeEvent<HTMLInputElement>) => onToggle(bill.bill_no);

  return (
    <TableRow hover>
      <TableCell padding="checkbox">
        <Checkbox
          checked={isChecked}
          onChange={handleToggle}
          slotProps={{ input: { "aria-label": `เลือกบิล ${bill.bill_no}` } }}
        />
      </TableCell>
      <TableCell>{bill.bill_no}</TableCell>
      <TableCell>{formatBuddhistShortDate(bill.bill_date)}</TableCell>
      <TableCell>{bill.salesperson_code ?? "-"}</TableCell>
      <TableCell align="right">{formatNumber(bill.bill_amount)}</TableCell>
      <TableCell align="right">{formatNumber(bill.outstanding_amount)}</TableCell>
      <TableCell>{formatBuddhistShortDate(bill.due_date) || "-"}</TableCell>
    </TableRow>
  );
}

/**
 * Picks the customer's credit bills an E1 receipt pays. Only bills with something still
 * outstanding are listed; each is added paying its whole outstanding, which the keyer can lower.
 */
export default function CreditBillDialog({
  open,
  params,
  pickedBillNos,
  onClose,
  onPick,
}: Readonly<CreditBillDialogProps>) {
  const [bills, setBills] = useState<ExpressCreditBill[]>([]);
  const [checkedBillNos, setCheckedBillNos] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { customer_code: customerCode, branch_code: branchCode, exclude_document_uuid: excludeUuid } = params;

  useEffect(() => {
    if (!open) return;
    let active = true;
    setCheckedBillNos([]);
    setLoadError(null);
    setLoading(true);
    getExpressCreditBills({
      customer_code: customerCode,
      branch_code: branchCode,
      exclude_document_uuid: excludeUuid,
    })
      .then(({ data: { data } }) => {
        if (active) setBills(data?.rows ?? []);
      })
      .catch((error: unknown) => {
        if (!active) return;
        // Shown in the dialog, not as a toast: an unset E1 start date must not read as "no bills".
        const message = apiErrorMessage(error);
        setBills([]);
        setLoadError(message ? localizeApiMessage(message) : FALLBACK_ERROR);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, customerCode, branchCode, excludeUuid]);

  const offeredBills = bills.filter((bill) => !pickedBillNos.includes(bill.bill_no));
  const isEmpty = !loading && !loadError && offeredBills.length === 0;
  const confirmLabel = checkedBillNos.length > 0 ? `เพิ่ม ${checkedBillNos.length} บิล` : "เพิ่มบิล";

  const handleToggle = (billNo: string) =>
    setCheckedBillNos((previous) =>
      previous.includes(billNo) ? previous.filter((checked) => checked !== billNo) : [...previous, billNo],
    );
  const handleConfirm = () => {
    onPick(offeredBills.filter((bill) => checkedBillNos.includes(bill.bill_no)));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>เลือกบิลเครดิตที่รับชำระ</DialogTitle>
      <DialogContent dividers>
        {loadError && <Alert severity="error">{loadError}</Alert>}
        {loading && (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        )}
        {isEmpty && (
          <Typography color="text.secondary" textAlign="center" py={4}>
            ลูกค้ารายนี้ไม่มีบิลเครดิตค้างชำระ
          </Typography>
        )}
        {!loading && offeredBills.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead sx={gridHeadSx}>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell sx={gridHeadCellSx}>ใบกำกับ#</TableCell>
                  <TableCell sx={gridHeadCellSx}>วันที่</TableCell>
                  <TableCell sx={gridHeadCellSx}>พนักงานขาย</TableCell>
                  <TableCell sx={gridHeadCellSx} align="right">
                    จำนวนเงิน
                  </TableCell>
                  <TableCell sx={gridHeadCellSx} align="right">
                    ยอดค้างชำระ
                  </TableCell>
                  <TableCell sx={gridHeadCellSx}>ครบกำหนด</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offeredBills.map((bill) => (
                  <CreditBillRow
                    key={bill.bill_no}
                    bill={bill}
                    isChecked={checkedBillNos.includes(bill.bill_no)}
                    onToggle={handleToggle}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ปิด</Button>
        <Button variant="contained" disabled={checkedBillNos.length === 0} onClick={handleConfirm}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
